import React, {
  useState,
  useCallback,
  useMemo,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import * as Sentry from '@sentry/browser';
import uuid from 'uuid';

import ReactMarkdown from 'react-markdown';

import { ClickAwayListener } from '@mui/material';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import ErrorIcon from '@mui/icons-material/Error';
import { ReactComponent as ThreeDots } from '../../assets/icons/three-dots.svg';
import { ReactComponent as FlagIcon } from '../../assets/icons/flag-icon.svg';

import AddToNotesMenu from '../../../components/AddToNotesMenu';
import ChatMessage from '../../Model/ChatMessage';

import { formatTimeForChatMessage } from '../../utils/time';
import { wrapURLsForMarkdown } from '../../utils/dataProcessing';
import { AttachmentType } from '../../utils/attachments';

import ChatUserProfilePicture from '../ChatUserProfilePicture';
import MessageActions from '../MessageActions/MessageActions';
import ChatChannelContext from '../../context/ChatChannelContext';
import ChatContext from '../../context/ChatContext';
import FirebaseContext from '../../context/FirebaseContext';
import EmojiPickerButton from '../../../components/EmojiInput';
import ReactionBar from '../ReactionBar';
import { AVIDA_SYSTEM_USER_ID } from '../../utils/reactions';
import { ChannelType } from '../../Model/ChatChannel';
import User from '../../../Model/User';
import ThreadIndicator from '../ThreadIndicator';
import ThreadModal from '../ThreadModal';
import useToast from '../../hooks/useToast';

import DeleteMessageModal from '../DeleteMessageModal';
import EditMessageModal from '../EditMessageModal';
import MessageTaskModal from '../MessageTaskModal';
import ShareAsHighlightModal from '../ShareAsHighlightModal';
import FlagAssigneeModal from '../FlagAssigneeModal';
import UnflagMessageModal from '../UnflagMessageModal';

import variants, { getMessageVariant } from './variants';

import AudioMessageAttachment from './components/Audio/AudioMessageAttachment';
import FileMessageAttachment from './components/File/FileMessageAttachment';
import VideoMessageAttachment from './components/Video/VideoMessageAttachment';
import ImageMessageAttachment from './components/Image/ImageMessageAttachment';
import GenericAttachment from './components/GenericAttachment/GenericAttachment';
import SystemMessagePreview from './components/SystemMessagePreview/SystemMessagePreview';

import {
  ContainerInbound,
  ContainerOutbound,
  SentAt,
  TextMediaContainer,
  ProfilePictureContainer,
  ThreeDotsButton,
  ThreeDotsContainer,
  ReactionTriggerButton,
  FlagIndicator,
  FlagAssigneeName,
  MessageColumn,
  RetryButton,
  InternalMessage,
  InternalMessageTitle,
  MessageDetailsContainer,
  EditedText,
  ReplyContainer,
  StyledPopper,
  TextWrapper,
  SenderNameInBubble,
} from './styles';
import { ModalType } from './constants';
import texts from './texts.json';

const MediaComponents = {
  [AttachmentType.AUDIO]: AudioMessageAttachment,
  [AttachmentType.FILE]: FileMessageAttachment,
  [AttachmentType.IMAGE]: ImageMessageAttachment,
  [AttachmentType.VIDEO]: VideoMessageAttachment,
};

// replayFailedMessage answers 409 when the server's mirror claim is already held by a concurrent
// attempt — that attempt will still deliver, so it is not a failure worth capturing.
const REPLAY_IN_FLIGHT_STATUS = 409;

const LinkRenderer = ({ href, children }) => (
  <a href={href} target="_blank" rel="noreferrer">
    {children}
  </a>
);

LinkRenderer.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const MessagePreview = ({
  message,
  variant,
  image,
  name,
  showSenderLabel,
  showAvatar,
  showInternalText,
  clientUserDoc,
  viewerId,
  isCoachAssistant,
  showACSenderNames,
  hideReplyPreview,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isPopperOpen, setIsPopperOpen] = useState(false);
  const [openModal, setOpenModal] = useState('');
  const [isRetryingSend, setIsRetryingSend] = useState(false);

  const {
    text,
    internalText,
    attachments,
    createdAt,
    isDeleted,
    isEdited,
    parent,
    initiatedByName,
    initiatedBy,
    userId,
    isFlagged,
    flagAssigneeName,
    reactions,
    deliveryStatus,
  } = message;

  const { activeChannel } = useContext(ChatChannelContext);
  const { chatUser } = useContext(ChatContext);
  const { firebase: { remote } } = useContext(FirebaseContext);
  const { showToast } = useToast();
  const isAdminCoaching = activeChannel?.type === ChannelType.ADMIN_COACHING;
  const avidaImageUrl = activeChannel?.memberData?.[AVIDA_SYSTEM_USER_ID]?.image || '';

  const handleRetrySend = useCallback(async () => {
    setIsRetryingSend(true);
    try {
      const response = await remote('adminCoachingChannel-replayFailedMessage', { messageId: message.id });
      // `remote` hands back the raw fetch Response, and fetch resolves on 4xx/5xx — so without
      // these checks every server rejection (409, 'not in a failed state', 'permanent error')
      // landed here as a silent success: spinner stops, message still reads as failed, no toast.
      if (response.status === REPLAY_IN_FLIGHT_STATUS) {
        showToast(texts.retryInFlight, { warning: true });
        return;
      }
      if (!response.ok) {
        throw new Error(`replayFailedMessage responded ${response.status}`);
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          errorMessage: error.message,
          messageId: message.id,
        },
      });
      showToast(texts.retryError, { error: true });
    } finally {
      setIsRetryingSend(false);
    }
  }, [remote, message.id, showToast]);

  const handleReactionSelect = useCallback((emoji) => {
    const currentReaction = message.reactions[viewerId];
    if (currentReaction === emoji) {
      message.removeReaction(viewerId);
    } else {
      message.setReaction(viewerId, emoji);
    }
  }, [message, viewerId]);

  const renderThreadMessage = useCallback((threadMessage) => {
    // Same sender resolution as the main feed: @Avida-mirrored messages carry the real Slack
    // sender, everything else falls back to the channel-member lookup.
    const isMirroredSender = !!threadMessage.actualSenderName;
    const memberInfo = activeChannel?.memberData?.[threadMessage.userId] || {};
    // Threads are short and every reply keeps its own avatar, so there is no run grouping here —
    // each mirrored reply just labels its sender.
    return (
      <MessagePreview
        key={threadMessage.id}
        message={threadMessage}
        variant={getMessageVariant(threadMessage, chatUser)}
        image={isMirroredSender ? threadMessage.actualSenderAvatarUrl : (memberInfo.image || '')}
        name={isMirroredSender ? threadMessage.actualSenderName : (memberInfo.name || '')}
        showSenderLabel={isMirroredSender}
        showInternalText={showInternalText}
        clientUserDoc={clientUserDoc}
        viewerId={viewerId}
        isCoachAssistant={isCoachAssistant}
        hideReplyPreview
      />
    );
  }, [activeChannel, chatUser, viewerId, showInternalText, clientUserDoc, isCoachAssistant]);

  const onClose = () => setOpenModal('');

  const handleActionMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setIsPopperOpen(true);
  };

  const handleActionMenuClose = () => {
    setAnchorEl(null);
    setIsPopperOpen(false);
  };

  const getAttachmentComponents = useCallback((attachmentList = []) => (
    attachmentList.slice().reverse().map((attachment) => {
      const Component = MediaComponents[attachment.type];
      return (
        <GenericAttachment
          key={uuid.v4()}
          attachment={attachment}
          AttachmentComponent={Component}
        />
      );
    })
  ), []);

  const media = useMemo(() => (
    getAttachmentComponents(attachments)
  ), [
    attachments,
    getAttachmentComponents,
  ]);

  const parentMedia = useMemo(() => (
    getAttachmentComponents(parent?.attachments)
  ), [
    parent,
    getAttachmentComponents,
  ]);

  const sentAtLabel = useMemo(() => {
    const time = formatTimeForChatMessage(createdAt);
    const isACMessage = isCoachAssistant && showACSenderNames && !!initiatedByName && initiatedBy !== userId;
    if (!isACMessage) return time;
    const senderLabel = initiatedBy === viewerId ? texts.you : initiatedByName;
    return `${senderLabel} • ${time}`;
  }, [createdAt, isCoachAssistant, showACSenderNames, initiatedByName, initiatedBy, userId, viewerId]);

  if (variant === variants.SYSTEM) {
    return <SystemMessagePreview message={message} />;
  }

  const Container = variant === variants.OUTBOUND ? ContainerOutbound : ContainerInbound;

  const flagIndicator = isFlagged ? (
    <FlagIndicator>
      <FlagIcon />
      {flagAssigneeName && <FlagAssigneeName>{flagAssigneeName}</FlagAssigneeName>}
    </FlagIndicator>
  ) : null;

  // Don't show the message container if there is no text and internalText is not visible
  if (!text && (!attachments || attachments.length === 0) && !showInternalText) {
    return null;
  }

  return (
    <Container data-testid={variant === variants.INBOUND ? 'message-received' : undefined}>
      <ProfilePictureContainer>
        {showAvatar && (
          <ChatUserProfilePicture image={image} name={name} size={30} displayIsOnline={false} />
        )}
      </ProfilePictureContainer>
      <MessageColumn>
        <TextMediaContainer $showTail={showAvatar}>
          {showSenderLabel && name && <SenderNameInBubble>{name}</SenderNameInBubble>}
          {isDeleted
            ? <div>{texts.deletedMessage}</div>
            : (
              <>
                <ThreeDotsContainer>
                  {flagIndicator}
                  <EmojiPickerButton
                    closeOnSelect
                    onChange={(emojiData) => handleReactionSelect(emojiData.emoji)}
                    renderTrigger={({ onClick }) => (
                      <ReactionTriggerButton type="button" onClick={onClick}>
                        <InsertEmoticonIcon fontSize="small" />
                      </ReactionTriggerButton>
                    )}
                  />
                  <ThreeDotsButton onClick={handleActionMenuOpen}>
                    <ThreeDots />
                  </ThreeDotsButton>
                  {anchorEl && (
                  <ClickAwayListener onClickAway={handleActionMenuClose}>
                    <StyledPopper
                      disablePortal
                      open={isPopperOpen}
                      anchorEl={anchorEl}
                      onClose={handleActionMenuClose}
                      placement={variant === variants.OUTBOUND ? 'left-start' : 'right-start'}
                    >
                      <MessageActions
                        closeActionMenu={handleActionMenuClose}
                        isOwnMessage={variant === variants.OUTBOUND}
                        setOpenModal={setOpenModal}
                        message={message}
                        clientUserDoc={clientUserDoc}
                      />
                    </StyledPopper>
                  </ClickAwayListener>
                  )}
                </ThreeDotsContainer>
                <AddToNotesMenu clientUserDoc={clientUserDoc} showAddToWorkoutPreferences>
                  <TextWrapper>
                    <ReactMarkdown components={{ a: LinkRenderer }}>
                      {wrapURLsForMarkdown(text?.trim())}
                    </ReactMarkdown>
                  </TextWrapper>
                  {(!!internalText && showInternalText) && (
                  <InternalMessage>
                    <InternalMessageTitle>{texts.internalMessageTitle}</InternalMessageTitle>
                    <TextWrapper>
                      <ReactMarkdown components={{ a: LinkRenderer }}>
                        {wrapURLsForMarkdown(internalText?.trim())}
                      </ReactMarkdown>
                    </TextWrapper>
                  </InternalMessage>
                  )}
                </AddToNotesMenu>
                {!!parent && !hideReplyPreview && (
                <ReplyContainer>
                  {parentMedia}
                  <TextWrapper>
                    {wrapURLsForMarkdown(parent.text?.trim())}
                  </TextWrapper>
                  {parent.internalText && (
                    <InternalMessage>
                      <InternalMessageTitle>{texts.internalMessageInReplyTitle}</InternalMessageTitle>
                      <TextWrapper>
                        <ReactMarkdown components={{ a: LinkRenderer }}>
                          {wrapURLsForMarkdown(parent.internalText?.trim())}
                        </ReactMarkdown>
                      </TextWrapper>
                    </InternalMessage>
                  )}
                </ReplyContainer>
                )}
                {media}
                <MessageDetailsContainer>
                  {/* Show "[AC name] • [time]" when another AC claimed the message,
                    "You • [time]" when the current viewer claimed it,
                    or just "[time]" for messages sent by the coach themselves.
                    Sender prefix only visible to assistant coaches. */}
                  <SentAt>
                    {sentAtLabel}
                  </SentAt>
                  {isEdited && (
                  <EditedText>
                    {texts.edited}
                  </EditedText>
                  )}
                </MessageDetailsContainer>
                <ReactionBar
                  reactions={reactions}
                  viewerId={viewerId}
                  onToggle={handleReactionSelect}
                  avidaImageUrl={avidaImageUrl}
                />
                {isAdminCoaching && (
                <ThreadIndicator
                  replyCount={message.replyCount}
                  onClick={() => setOpenModal(ModalType.THREAD)}
                />
                )}
              </>
            )}
        </TextMediaContainer>
        {isAdminCoaching && variant === variants.OUTBOUND && deliveryStatus === 'failed' && (
          <RetryButton type="button" onClick={handleRetrySend} disabled={isRetryingSend}>
            <ErrorIcon />
            {isRetryingSend ? texts.retrying : texts.failedRetry}
          </RetryButton>
        )}
      </MessageColumn>
      {openModal === ModalType.DELETE && (
        <DeleteMessageModal message={message} onClose={onClose} isOpen />
      )}
      {openModal === ModalType.EDIT && (
        <EditMessageModal message={message} onClose={onClose} isOpen />
      )}
      {openModal === ModalType.CREATE_COACH_TASK && (
        <MessageTaskModal message={message} onClose={onClose} isOpen />
      )}
      {openModal === ModalType.SHARE_AS_HIGHLIGHT && (
        <ShareAsHighlightModal
          userId={activeChannel?.userId}
          viewAsCoachId={activeChannel?.coachId}
          clientName={activeChannel?.memberData?.[activeChannel?.userId]?.name || ''}
          quoteSource={{ channelId: message.channelId, messageId: message.id, text: message.text }}
          onClose={onClose}
          isOpen
        />
      )}
      {openModal === ModalType.FLAG && (
        <FlagAssigneeModal message={message} onClose={onClose} isOpen />
      )}
      {openModal === ModalType.UNFLAG && (
        <UnflagMessageModal message={message} onClose={onClose} isOpen />
      )}
      {openModal === ModalType.THREAD && (
        <ThreadModal
          rootMessage={message}
          isOpen
          onClose={onClose}
          renderMessage={renderThreadMessage}
        />
      )}
    </Container>
  );
};

MessagePreview.propTypes = {
  message: PropTypes.instanceOf(ChatMessage).isRequired,
  variant: PropTypes.oneOf(Object.values(variants)).isRequired,
  image: PropTypes.string,
  name: PropTypes.string,
  showSenderLabel: PropTypes.bool,
  showAvatar: PropTypes.bool,
  showInternalText: PropTypes.bool.isRequired,
  clientUserDoc: PropTypes.instanceOf(User),
  viewerId: PropTypes.string,
  isCoachAssistant: PropTypes.bool,
  showACSenderNames: PropTypes.bool,
  hideReplyPreview: PropTypes.bool,
};

MessagePreview.defaultProps = {
  image: '',
  name: '',
  showSenderLabel: false,
  showAvatar: true,
  clientUserDoc: null,
  viewerId: '',
  isCoachAssistant: false,
  showACSenderNames: false,
  hideReplyPreview: false,
};

export default observer(MessagePreview);
